"use client";

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

type ServiceSolutionSlugsContextValue = {
  serviceSlugs: ReadonlySet<string>;
  solutionSlugs: ReadonlySet<string>;
};

const EMPTY = new Set<string>();

const ServiceSolutionSlugsContext =
  createContext<ServiceSolutionSlugsContextValue>({
    serviceSlugs: EMPTY,
    solutionSlugs: EMPTY,
  });

function toSlugSet(slugs: string[] | undefined): ReadonlySet<string> {
  const set = new Set<string>();
  for (const raw of slugs ?? []) {
    const slug = String(raw || "")
      .trim()
      .replace(/^\/+|\/+$/g, "")
      .toLowerCase();
    if (slug) set.add(slug);
  }
  return set;
}

export function ServiceSolutionSlugsProvider({
  serviceSlugs,
  solutionSlugs,
  children,
}: {
  serviceSlugs?: string[];
  solutionSlugs?: string[];
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      serviceSlugs: toSlugSet(serviceSlugs),
      solutionSlugs: toSlugSet(solutionSlugs),
    }),
    [serviceSlugs, solutionSlugs]
  );

  return (
    <ServiceSolutionSlugsContext.Provider value={value}>
      {children}
    </ServiceSolutionSlugsContext.Provider>
  );
}

export function useServiceSolutionSlugs(): ServiceSolutionSlugsContextValue {
  return useContext(ServiceSolutionSlugsContext);
}
